/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-non-null-assertion
// tslint:disable:no-magic-numbers

import { ElementRef, Injectable } from '@angular/core';
import { getGtoEntryKey, GtoEntryPoint } from '@gh/builder-shared';
import {
  GraphicalTakeOffHousePlanSaveDto,
  GraphicalTakeOffHousePlanViewRestService,
  GraphicalTakeOffPictureRestService, GraphicalTakeOffPoint,
  GraphicalTakeOffTopRestService,
  OfficeMeasurement,
} from '@gh/builder-shared-data';
import { ID } from '@gh/common-shared-data';
import { Enum } from '@gh/core-data';
import { DialogService } from '@gh/core-ui';
import { combineLatest$, of$ } from '@gh/rx/index';
import { map$, mapTo$ } from '@gh/rx/operators';
import { isNil, omit } from 'lodash';
import { Layer, PaperScope, Path, Point, Raster, Rectangle, Shape, View } from 'paper';
import { Observable, Subject } from 'rxjs';
import {
  GraphicalTakeOffHousePlanSave,
  GraphicalTakeOffHousePlanView,
  graphicalTakeOffHousePlanViewFromDto,
  GraphicalTakeOffPicture,
  GraphicalTakeOffTop,
  GraphicalTakeOffTopView,
  graphicalTakeOffTopViewFromDto,
  GraphicalTakeOffViewInfo, newPoint, pointLength,
} from './models';

export const GTO_MEASUREMENT_TYPE_LENGTH: string = 'LENGTH';
export const GTO_MEASUREMENT_TYPE_AREA: string = 'AREA';
export const GTO_MEASUREMENT_TYPE_COUNT: string = 'COUNT';

export enum GraphicalTakeOffMeasurementStatus {
  STARTING = 'STARTING',
  INCOMPLETE = 'INCOMPLETE',
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN_PROGRESS',
}

/**
 * GTO business service
 */
@Injectable()
export class GraphicalTakeOffBusinessService {
  /**
   * The PaperScope represents the scope associated with a Paper context
   * See: http://paperjs.org/reference/paperscope/
   *
   * @type {"paper".PaperScope}
   */
  scope: PaperScope;

  /**
   * Canvas element
   * See: http://paperjs.o.org/reference/paperscope/#setup-element
   *
   * @type {ElementRef}
   */
  canvasElement: ElementRef;

  /**
   * Constructor
   *
   * @param {DialogService} dialogService - Dialog service
   * @param {GraphicalTakeOffHousePlanViewRestService} viewService View REST service
   * @param {GraphicalTakeOffTop} topService TOP  REST service
   * @param {GraphicalTakeOffPicture} pictureService Picture  REST service
   */
  constructor(
    private dialogService: DialogService,
    private viewService: GraphicalTakeOffHousePlanViewRestService,
    private topService: GraphicalTakeOffTopRestService,
    private pictureService: GraphicalTakeOffPictureRestService,
  ) {
  }

  /**
   * Get the raster item object.
   *
   * See: `getItem`
   *
   * @returns {"paper".Raster} - Raster
   */
  get raster(): Raster {
    return this.getItem('id', 'raster');
  }

  /**
   * Get the raster layer object.
   *
   * See: `getItem`
   *
   * @returns {"paper".Layer} - Raster layer
   */
  get rasterLayer(): Layer {
    return this.getItem('id', 'rasterLayer');
  }

  /**
   * Set canvas element
   *
   * @param {ElementRef} element Canvas element
   * @returns {ElementRef} Canvas element
   */
  setCanvasElement(element: ElementRef): ElementRef {
    this.canvasElement = element;
    return this.canvasElement;
  }

  setPaperScope(scope: PaperScope): void {
    this.scope = scope;
  }

  /**
   * Get project item(s) using metadata `id` property
   * See: http://paperjs.org/reference/project/#getitems-options
   *
   * @param {string} key - Metadata item key
   * @param {string} value - Metadata item value
   * @returns {any} - Item(s)
   */
  getItem(key: string, value: string): any {
    const data = {};
    data[key] = value;

    const items = this.scope.project.getItems({
      data,
    });

    return items.length > 1 ? items : items.pop();
  }

  /**
   * Is imperial units?
   *
   * @returns {boolean}
   */
  isImperialUnits(view: GraphicalTakeOffHousePlanView): boolean {
    return Enum.toValue(OfficeMeasurement, 'Imperial') === view.measurementScale;
  }

  /**
   * Find all house plan views by house plan id
   *
   * @param {ID} hpId - House plan id
   * @param {string} entryPoint - Entry point
   * @returns {Observable<GraphicalTakeOffHousePlanView[]>} - List of house plan views
   */
  findAllViews(hpId: ID, entryPoint: string): Observable<GraphicalTakeOffHousePlanView[]> {
    return this.viewService.findAll(hpId, entryPoint).pipe(
      map$((views) => views.map(graphicalTakeOffHousePlanViewFromDto)));
  }

  /**
   * Get house plan view by id
   *
   * @param {ID} hpId - House plan id
   * @param {ID} hpViewId - House plan view id
   * @param {string} entryPoint - Entry point
   * @param {boolean} useSnapToPoint - Use snap to point
   * @returns {Observable<GraphicalTakeOffHousePlanView>} - House plan view
   */
  getView(hpId: ID, hpViewId: ID, entryPoint: string):
    Observable<GraphicalTakeOffHousePlanView> {
    return this.viewService.get(hpId, hpViewId, entryPoint, false)
      .pipe(map$(graphicalTakeOffHousePlanViewFromDto));
  }

  getViewSnapGrid(hpId: ID, hpViewId: ID, entryPoint: string): Observable<GraphicalTakeOffPoint[]> {
    return this.viewService.get(hpId, hpViewId, entryPoint, true).pipe(map$(({ corners }) => corners));
  }

  /**
   * Create house plan view
   *
   * @param {ID} hpId - House plan id
   * @param {GraphicalTakeOffHousePlanView} hpView - House plan view
   * @param {string} entryPoint - Entry point
   * @returns {Observable<GraphicalTakeOffHousePlanView>} - New house plan view
   */
  createHousePlanView(hpId: ID, hpView: GraphicalTakeOffHousePlanView, entryPoint: string):
    Observable<GraphicalTakeOffHousePlanView> {
    return this.viewService.create(hpId, hpView, entryPoint).pipe(map$(graphicalTakeOffHousePlanViewFromDto));
  }

  /**
   * Update house plan view
   *
   * @param {ID} hpId - House plan id
   * @param {ID} hpViewId - House plan view id
   * @param {any} data - Picture data
   * @param {string} entryPoint - Entry point
   * @param {boolean} useSnapToPoint - Use snap to point
   * @returns {Observable<GraphicalTakeOffHousePlanView>} - New house plan view
   */
  updateHousePlanView(hpId: ID,
                      hpViewId: ID,
                      data: GraphicalTakeOffHousePlanSave,
                      entryPoint: string,
                      useSnapToPoint: boolean = true): Observable<GraphicalTakeOffHousePlanView> {
    // @ts-ignore
    return this.viewService.update(hpId, hpViewId, omit(data, 'viewId'), entryPoint, useSnapToPoint)
      .pipe(map$(graphicalTakeOffHousePlanViewFromDto));
  }

  /**
   * Delete house plan view
   *
   * @param {ID} hpId - House plan id
   * @param {ID} hpViewId - House plan view id
   * @param {string} entryPoint - Entry point
   * @returns {Observable<GraphicalTakeOffHousePlanView>} - Response
   */
  deleteHousePlanView(hpId: ID,
                      hpViewId: ID,
                      data: any,
                      entryPoint: string): Observable<void> {
    return this.viewService.delete(hpId, hpViewId, data, entryPoint);
  }

  /**
   * Find all TOPs by house plan id
   *
   * @param {ID} hpId - House plan id
   * @param {string} viewId - View ID
   * @param {string} entryPoint - Entry point
   * @param {string} takeOffParamId - TOP id
   * @returns {Observable<GraphicalTakeOffTop[]>} - List of house plan TOPs
   */
  findAllTops(hpId: ID,
              viewId: ID,
              entryPoint: GtoEntryPoint,
              takeOffParamId?: string): Observable<GraphicalTakeOffTop[]> {
    const viewIdName = getGtoEntryKey(entryPoint, 'viewId');
    return this.topService.findAll(hpId, viewIdName, viewId, entryPoint, takeOffParamId);
  }

  /**
   * Fetch all TOPs by house plan view id
   *
   * @param {ID} hpId - House plan id
   * @param {ID} hpViewId - House plan view id
   * @param {string} entryPoint - Entry point
   * @returns {Observable<GraphicalTakeOffTop[]>} - List of house plan view TOPs
   */
  findAllTopsView(hpId: ID, hpViewId: ID, entryPoint: string): Observable<GraphicalTakeOffTopView[]> {
    return this.topService.findAllView(hpId, hpViewId, entryPoint).pipe(
      map$((items) => items.map(graphicalTakeOffTopViewFromDto)));
  }

  /**
   * Get TOP by id
   *
   * @param {ID} hpId - House plan id
   * @param {ID} topId - TOP id
   * @param {string} entryPoint - Entry point
   * @returns {Observable<GraphicalTakeOffTop>} - TOP
   */
  getTop(hpId: ID, topId: ID, entryPoint: string): Observable<GraphicalTakeOffTop> {
    return this.topService.get(hpId, topId, entryPoint);
  }

  /**
   * Get picture by key
   *
   * @param {string} key - Picture key
   * @returns {Observable<GraphicalTakeOffPicture>} - Picture
   */
  getPicture(key: string): Observable<GraphicalTakeOffPicture> {
    return this.pictureService.get(key);
  }

  /**
   * Create image from PDF file
   *
   * @param {string} key - File key
   * @param {number} page - Page number
   * @returns {Observable<GraphicalTakeOffPicture>} - Picture
   */
  createPicture(key: string, page: number): Observable<GraphicalTakeOffPicture> {
    return this.pictureService.create(key, page);
  }

  findViewData(id: ID,
               viewId: ID,
               entryPoint: GtoEntryPoint): Observable<GraphicalTakeOffViewInfo> {
    const view$ = this.getView(id, viewId, entryPoint);
    const tops$ = this.findAllTops(id, viewId, entryPoint, void 0);
    const topViews$ = isNil(viewId) ? of$([]) : this.findAllTopsView(id, viewId, entryPoint);

    return combineLatest$(view$, tops$, topViews$).pipe(map$(([view, tops, topViews]) => ({
      view: graphicalTakeOffHousePlanViewFromDto(view),
      tops,
      topViews,
    })));
  }
}
